import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  ImagePlus,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  AI_MODEL_MANAGER_CATEGORIES,
  type AiModelManagerCategory,
  getPredictionHistoryDetail,
  getPredictionOutput,
  getPredictionOutputFromResultPayload,
  listPredictionHistory,
  type PredictionHistoryDetail,
  type PredictionHistoryEntry,
  pollPredictionJob,
  type PredictionJobResponse,
  type PredictionJobStatus,
  submitPredictionJob,
} from "@/lib/aiModelManager";
import { cn } from "@/lib/utils";

type FormItem = {
  id: string;
  itemName: string;
  caption: string;
  price: string;
  category: AiModelManagerCategory | "";
  file: File | null;
  previewUrl: string | null;
};

type SubmissionState =
  | "idle"
  | "validating"
  | "submitting"
  | "queued"
  | "processing"
  | "completed"
  | "failed";

type ValidatedItem = {
  item_name: string;
  caption: string;
  price: number;
  category: AiModelManagerCategory;
  original_filename: string;
  imageFile: File;
};

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

export function FashionDesignPredictionPage() {
  const [items, setItems] = useState<FormItem[]>([createEmptyFormItem(1)]);
  const [submissionState, setSubmissionState] =
    useState<SubmissionState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [job, setJob] = useState<PredictionJobResponse | null>(null);
  const [historyEntries, setHistoryEntries] = useState<PredictionHistoryEntry[]>(
    []
  );
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const [selectedHistory, setSelectedHistory] =
    useState<PredictionHistoryDetail | null>(null);

  const itemCounterRef = useRef(2);
  const pollControllerRef = useRef<AbortController | null>(null);
  const latestJobIdRef = useRef<string | null>(null);
  const currentItemsRef = useRef<FormItem[]>(items);

  useEffect(() => {
    currentItemsRef.current = items;
  }, [items]);

  useEffect(() => {
    return () => {
      pollControllerRef.current?.abort();
      for (const item of currentItemsRef.current) {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      }
    };
  }, []);

  useEffect(() => {
    void refreshHistory();
  }, []);

  const predictionOutput = useMemo(() => getPredictionOutput(job), [job]);
  const historyPredictionOutput = useMemo(
    () => getPredictionOutputFromResultPayload(selectedHistory?.result_payload),
    [selectedHistory]
  );
  const activePredictionOutput = historyPredictionOutput ?? predictionOutput;
  const activeResultRows = activePredictionOutput?.results ?? [];
  const isBusy =
    submissionState === "validating" ||
    submissionState === "submitting" ||
    submissionState === "queued" ||
    submissionState === "processing";

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    pollControllerRef.current?.abort();
    setErrorMessage(null);
    setJob(null);

    try {
      setSubmissionState("validating");
      const validatedItems = validateItems(items);
      setSelectedHistory(null);
      setSelectedHistoryId(null);

      setSubmissionState("submitting");
      const submittedJob = await submitPredictionJob({
        clientCorrelationId: `fashion-design-prediction-${Date.now()}`,
        items: validatedItems,
      });

      latestJobIdRef.current = submittedJob.job_id;
      setJob(submittedJob);
      setSubmissionState(mapJobStatusToSubmissionState(submittedJob.status));

      const controller = new AbortController();
      pollControllerRef.current = controller;

      const finalJob = await pollPredictionJob(submittedJob.job_id, {
        intervalMs: POLL_INTERVAL_MS,
        timeoutMs: POLL_TIMEOUT_MS,
        signal: controller.signal,
        onUpdate: (nextJob) => {
          if (latestJobIdRef.current !== nextJob.job_id) {
            return;
          }

          setJob(nextJob);
          setSubmissionState(mapJobStatusToSubmissionState(nextJob.status));
        },
      });

      if (latestJobIdRef.current === finalJob.job_id) {
        setJob(finalJob);
        setSubmissionState(mapJobStatusToSubmissionState(finalJob.status));
      }
      await refreshHistory();
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      setSubmissionState("failed");
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Unable to submit the prediction job."
      );
    }
  }

  function handleAddItem() {
    const nextIndex = itemCounterRef.current;
    itemCounterRef.current += 1;
    setItems((currentItems) => [...currentItems, createEmptyFormItem(nextIndex)]);
  }

  function handleRemoveItem(itemId: string) {
    setItems((currentItems) => {
      if (currentItems.length === 1) {
        return currentItems;
      }

      const targetItem = currentItems.find((item) => item.id === itemId);
      if (targetItem?.previewUrl) {
        URL.revokeObjectURL(targetItem.previewUrl);
      }

      return currentItems.filter((item) => item.id !== itemId);
    });
  }

  function updateItem(
    itemId: string,
    updater: (item: FormItem) => FormItem
  ) {
    setItems((currentItems) =>
      currentItems.map((item) => (item.id === itemId ? updater(item) : item))
    );
  }

  function handleFileChange(itemId: string, file: File | null) {
    updateItem(itemId, (item) => {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }

      return {
        ...item,
        file,
        previewUrl: file ? URL.createObjectURL(file) : null,
      };
    });
  }

  async function refreshHistory() {
    try {
      setHistoryLoading(true);
      setHistoryError(null);
      const rows = await listPredictionHistory({ days: 3, limit: 50 });
      setHistoryEntries(rows);
    } catch (error) {
      setHistoryError(
        error instanceof Error
          ? error.message
          : "Unable to load recent prediction history."
      );
    } finally {
      setHistoryLoading(false);
    }
  }

  async function handleSelectHistory(generationId: string) {
    try {
      setHistoryDetailLoading(true);
      setHistoryError(null);
      setSelectedHistoryId(generationId);
      const detail = await getPredictionHistoryDetail(generationId);
      setSelectedHistory(detail);
    } catch (error) {
      setHistoryError(
        error instanceof Error
          ? error.message
          : "Unable to load selected history record."
      );
    } finally {
      setHistoryDetailLoading(false);
    }
  }

  return (
    <div className="w-full min-w-0 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle>Fashion Design Prediction</CardTitle>
              <CardDescription>
                Upload one or more product images, provide price, category, and
                caption, then let the backend generate transformed images and
                score predicted share demand asynchronously.
              </CardDescription>
            </div>
            <Badge variant="secondary" className="gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              AI Hub
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {errorMessage ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Submission failed</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      <form className="space-y-6" onSubmit={handleSubmit}>
        {items.map((item, index) => (
          <Card key={item.id}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">{`Item ${index + 1}`}</CardTitle>
                  <CardDescription>
                    Provide the source image and product details for this
                    prediction input.
                  </CardDescription>
                </div>
                {items.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveItem(item.id)}
                    aria-label={`Remove item ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid min-w-0 gap-6 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)]">
                <div className="min-w-0 space-y-3">
                  <Label htmlFor={`image-${item.id}`}>Product image</Label>
                  <div className="flex aspect-square items-center justify-center overflow-hidden rounded-lg border border-dashed bg-muted/30">
                    {item.previewUrl ? (
                      <img
                        src={item.previewUrl}
                        alt={`${item.itemName || `Item ${index + 1}`} preview`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2 px-6 text-center text-muted-foreground">
                        <ImagePlus className="h-8 w-8" />
                        <p className="text-sm">
                          Upload a product image to start this item.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Input
                      id={`image-${item.id}`}
                      className="overflow-hidden"
                      type="file"
                      accept="image/*"
                      onChange={(event) =>
                        handleFileChange(
                          item.id,
                          event.target.files?.[0] ?? null
                        )
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Supported input: one product image per card.
                    </p>
                  </div>
                </div>

                <div className="grid min-w-0 gap-4 md:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor={`item-name-${item.id}`}>Item label</Label>
                    <Input
                      id={`item-name-${item.id}`}
                      placeholder={`Item ${index + 1}`}
                      value={item.itemName}
                      onChange={(event) =>
                        updateItem(item.id, (currentItem) => ({
                          ...currentItem,
                          itemName: event.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor={`price-${item.id}`}>Price</Label>
                    <Input
                      id={`price-${item.id}`}
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      placeholder="129.90"
                      value={item.price}
                      onChange={(event) =>
                        updateItem(item.id, (currentItem) => ({
                          ...currentItem,
                          price: event.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="grid gap-2 md:col-span-2">
                    <Label htmlFor={`category-${item.id}`}>Category</Label>
                    <Select
                      value={item.category}
                      onValueChange={(value) =>
                        updateItem(item.id, (currentItem) => ({
                          ...currentItem,
                          category: value as AiModelManagerCategory,
                        }))
                      }
                    >
                      <SelectTrigger id={`category-${item.id}`}>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {AI_MODEL_MANAGER_CATEGORIES.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2 md:col-span-2">
                    <Label htmlFor={`caption-${item.id}`}>Caption</Label>
                    <Textarea
                      id={`caption-${item.id}`}
                      placeholder="Describe the product design, fabric, silhouette, and any other key visual details."
                      value={item.caption}
                      onChange={(event) =>
                        updateItem(item.id, (currentItem) => ({
                          ...currentItem,
                          caption: event.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        <Card className="border-dashed">
          <CardContent className="p-0">
            <Button
              type="button"
              variant="ghost"
              className="h-auto w-full min-w-0 justify-start rounded-lg p-6 text-left"
              onClick={handleAddItem}
            >
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Plus className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <div className="font-medium">Add another product item</div>
                  <div className="text-sm text-muted-foreground">
                    Create an additional form card to predict more products in
                    the same request.
                  </div>
                </div>
              </div>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between">
            <div className="space-y-1">
              <div className="font-medium">Submit prediction job</div>
              <div className="text-sm text-muted-foreground">
                The request is processed asynchronously and the page will keep
                polling until the prediction completes or fails.
              </div>
            </div>
            <Button type="submit" disabled={isBusy}>
              {isBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {getSubmitButtonLabel(submissionState)}
            </Button>
          </CardContent>
        </Card>
      </form>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent generations (last 3 days)</CardTitle>
          <CardDescription>
            Click a generation timestamp to load a previous prediction snapshot.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {historyError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>History unavailable</AlertTitle>
              <AlertDescription>{historyError}</AlertDescription>
            </Alert>
          ) : null}

          {historyLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : null}

          {!historyLoading && historyEntries.length === 0 ? (
            <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              No recent generations found for the last 3 days.
            </div>
          ) : null}

          {!historyLoading && historyEntries.length > 0 ? (
            <div className="space-y-2">
              {historyEntries.map((entry) => (
                <Button
                  key={entry.generation_id}
                  type="button"
                  variant="ghost"
                  className={cn(
                    "h-auto w-full justify-start rounded-lg border px-4 py-3 text-left",
                    selectedHistoryId === entry.generation_id &&
                      "border-primary bg-primary/5"
                  )}
                  onClick={() => void handleSelectHistory(entry.generation_id)}
                >
                  <div className="flex w-full items-center justify-between gap-3">
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium">
                        {entry.generated_at_local_label} (+08)
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {entry.item_count ?? 0} item(s)
                      </div>
                    </div>
                    {typeof entry.share_sum === "number" ? (
                      <Badge variant="outline">
                        Share: {entry.share_sum.toFixed(4)}
                      </Badge>
                    ) : null}
                  </div>
                </Button>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-lg">Prediction result</CardTitle>
              <CardDescription>
                Track job progress and review the ranked predicted share output
                after Runpod finishes scoring the products.
              </CardDescription>
            </div>
            {selectedHistory ? (
              <Badge variant="outline">History</Badge>
            ) : job ? (
              <Badge variant={getStatusBadgeVariant(job.status)}>
                {formatJobStatus(job.status)}
              </Badge>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!job && !selectedHistory && submissionState === "idle" ? (
            <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
              No prediction submitted yet. Complete the form above and submit a
              job to see status updates and result rankings here.
            </div>
          ) : null}

          {selectedHistory ? (
            <div className="grid min-w-0 gap-3 md:grid-cols-3">
              <JobMetaCard
                icon={<Clock3 className="h-4 w-4" />}
                label="Generation ID"
                value={selectedHistory.generation_id}
              />
              <JobMetaCard
                icon={<Sparkles className="h-4 w-4" />}
                label="Generated (+08)"
                value={selectedHistory.generated_at_local_label || "Unknown"}
              />
              <JobMetaCard
                icon={<CheckCircle2 className="h-4 w-4" />}
                label="Source"
                value="Historical snapshot"
              />
            </div>
          ) : job ? (
            <div className="grid min-w-0 gap-3 md:grid-cols-3">
              <JobMetaCard
                icon={<Clock3 className="h-4 w-4" />}
                label="Job ID"
                value={job.job_id}
              />
              <JobMetaCard
                icon={<Sparkles className="h-4 w-4" />}
                label="Created"
                value={job.created_at ?? "Pending"}
              />
              <JobMetaCard
                icon={<CheckCircle2 className="h-4 w-4" />}
                label="Completed"
                value={job.completed_at ?? "In progress"}
              />
            </div>
          ) : null}

          {!selectedHistory &&
          (submissionState === "submitting" ||
            submissionState === "queued" ||
            submissionState === "processing") && (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <p className="text-sm text-muted-foreground">
                {getProgressMessage(submissionState)}
              </p>
            </div>
          )}

          {historyDetailLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <p className="text-sm text-muted-foreground">
                Loading historical prediction result...
              </p>
            </div>
          ) : null}

          {!selectedHistory && submissionState === "failed" && !errorMessage ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Prediction job failed</AlertTitle>
              <AlertDescription>
                {getJobFailureMessage(job) ??
                  "The backend reported a failed prediction job."}
              </AlertDescription>
            </Alert>
          ) : null}

          {!selectedHistory &&
          submissionState === "completed" &&
          predictionOutput?.success === false ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Runpod returned an error</AlertTitle>
              <AlertDescription>
                {predictionOutput.error ??
                  "The prediction worker completed without a successful output payload."}
              </AlertDescription>
            </Alert>
          ) : null}

          {(selectedHistory || submissionState === "completed") &&
          activePredictionOutput?.success !== false &&
          activeResultRows.length > 0 ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">
                  {activePredictionOutput.item_count ?? activeResultRows.length} item(s)
                </Badge>
                {typeof activePredictionOutput.share_sum === "number" ? (
                  <Badge variant="outline">
                    Share sum: {activePredictionOutput.share_sum.toFixed(4)}
                  </Badge>
                ) : null}
                <Badge variant="outline">
                  {selectedHistory ? "History snapshot" : "Live job"}
                </Badge>
              </div>

              <div className="min-w-0 overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rank</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Predicted Share</TableHead>
                      <TableHead className="text-right">Predicted Share %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeResultRows.map((row, index) => (
                      <TableRow key={`${row.item_name}-${index}`}>
                        <TableCell>{row.rank_share_order ?? index + 1}</TableCell>
                        <TableCell className="font-medium">
                          {row.item_name}
                        </TableCell>
                        <TableCell>{row.category}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(row.price)}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.predicted_share.toFixed(4)}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.predicted_share_pct.toFixed(2)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

function JobMetaCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border bg-muted/20 p-4">
      <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="break-all text-sm font-medium">{value}</div>
    </div>
  );
}

function createEmptyFormItem(index: number): FormItem {
  return {
    id: `prediction-item-${index}`,
    itemName: `Item ${index}`,
    caption: "",
    price: "",
    category: "",
    file: null,
    previewUrl: null,
  };
}

function validateItems(items: FormItem[]): ValidatedItem[] {
  return items.map((item, index) => {
    const itemNumber = index + 1;
    const caption = item.caption.trim();
    const price = Number(item.price);

    if (!item.file) {
      throw new Error(`Item ${itemNumber} is missing an image.`);
    }

    if (!caption) {
      throw new Error(`Item ${itemNumber} is missing a caption.`);
    }

    if (!item.category) {
      throw new Error(`Item ${itemNumber} is missing a category.`);
    }

    if (!Number.isFinite(price) || price < 0) {
      throw new Error(`Item ${itemNumber} must include a valid price.`);
    }

    return {
      item_name: item.itemName.trim() || `Item ${itemNumber}`,
      caption,
      price,
      category: item.category,
      original_filename: item.file.name,
      imageFile: item.file,
    };
  });
}

function getSubmitButtonLabel(state: SubmissionState): string {
  switch (state) {
    case "idle":
    case "failed":
    case "completed":
      return "Start prediction";
    case "validating":
      return "Validating input";
    case "submitting":
      return "Submitting job";
    case "queued":
      return "Job queued";
    case "processing":
      return "Processing prediction";
    default:
      return assertNever(state);
  }
}

function getProgressMessage(state: SubmissionState): string {
  switch (state) {
    case "submitting":
      return "Uploading the request to the backend.";
    case "queued":
      return "The job is queued and waiting for the worker pipeline to start.";
    case "processing":
      return "Images are being transformed and the Runpod worker is preparing prediction output.";
    case "idle":
    case "validating":
    case "completed":
    case "failed":
      return "";
    default:
      return assertNever(state);
  }
}

function mapJobStatusToSubmissionState(status: PredictionJobStatus): SubmissionState {
  switch (status) {
    case "queued":
      return "queued";
    case "processing":
      return "processing";
    case "failed":
      return "failed";
    case "completed":
      return "completed";
    default:
      return assertNever(status);
  }
}

function formatJobStatus(status: PredictionJobStatus): string {
  switch (status) {
    case "queued":
      return "Queued";
    case "processing":
      return "Processing";
    case "failed":
      return "Failed";
    case "completed":
      return "Completed";
    default:
      return assertNever(status);
  }
}

function getStatusBadgeVariant(status: PredictionJobStatus) {
  switch (status) {
    case "queued":
    case "processing":
      return "secondary" as const;
    case "failed":
      return "destructive" as const;
    case "completed":
      return "default" as const;
    default:
      return assertNever(status);
  }
}

function getJobFailureMessage(job: PredictionJobResponse | null): string | null {
  const errorPayload = job?.error_payload;
  if (!errorPayload || typeof errorPayload !== "object") {
    return null;
  }

  const error = (errorPayload as Record<string, unknown>).error;
  return typeof error === "string" ? error : null;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-MY", {
    style: "currency",
    currency: "MYR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function assertNever(value: never): never {
  throw new Error(`Unhandled value: ${String(value)}`);
}
